import {
  alignmentWalkAtom,
  alignmentsAtom,
  DEFAULT_WALK_STEP,
  selectedAlignmentKeyAtom,
  selectedLandXMLFeatureAtom,
  selectedLayersAtom,
} from '@/atoms';
import { degradationList, formatStation, internalToDisplay, projectPoint } from '@/core/alignment';
import { useFeatureCheck } from '@/hooks/useFeature';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, type FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ID } from './ID';
import { Button } from './ui/button';
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from './ui/drawer';
import { Label } from './ui/label';

const LandXMLProperties: FunctionComponent = () => {
  const { projectId } = useParams();
  const [selected, setSelected] = useAtom(selectedLandXMLFeatureAtom);
  const layers = useAtomValue(selectedLayersAtom(projectId!));
  const layer = layers.find((l) => l.id === selected?.layerId);
  const { t } = useTranslation('components');
  // The degradation copy lives in the `project` namespace with the rest of the
  // alignment/station strings, which this drawer otherwise doesn't use.
  const { t: tp } = useTranslation('project');

  const alignments = useAtomValue(alignmentsAtom);
  const setWalk = useSetAtom(alignmentWalkAtom);
  const checkFeature = useFeatureCheck();
  // The registry holds world-space alignments; `clickPoint` is world-space too.
  const ownAlignment = selected ? alignments.get(`${selected.layerId}:${selected.featureId}`) : undefined;
  const station = useMemo(() => {
    if (!selected || !ownAlignment || !selected.clickPoint) return null;
    const c = selected.clickPoint;
    return projectPoint(ownAlignment, c.x, c.y, c.z);
  }, [selected, ownAlignment]);
  const nearest = selected?.nearestAlignment ? alignments.get(selected.nearestAlignment.key) : undefined;

  // The orange highlight + station ticks are driven by `selectedAlignmentKey`,
  // which LandXML.tsx / GeoJson.tsx set on pick. It belongs to the drawer's
  // lifetime, so drop it whenever the drawer has no selection — closing it,
  // starting a walk, or the selection being cleared from anywhere else.
  const setSelectedAlignmentKey = useSetAtom(selectedAlignmentKeyAtom);
  useEffect(() => {
    if (!selected) setSelectedAlignmentKey(null);
  }, [selected, setSelectedAlignmentKey]);

  const _startWalk = (key: string, internalStation: number) => {
    // Soft paywall: the button stays clickable and opens the upgrade dialog.
    const gate = checkFeature('viewer.alignmentWalk');
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    setWalk({ key, station: internalStation, step: DEFAULT_WALK_STEP, snapKeyPoints: false, follow: true });
    setSelected(null);
    setSelectedAlignmentKey(null);
  };

  const handleClose = () => {
    setSelected(null);
    setSelectedAlignmentKey(null);
  };

  if (!selected) {
    return (
      <Drawer open={false} onClose={handleClose}>
        <DrawerContent />
      </Drawer>
    );
  }
  const m = selected.metadata;

  return (
    <Drawer open={true} onClose={handleClose}>
      <DrawerContent>
        <DrawerHeader className='w-lg'>
          <DrawerTitle>{t('landxmlProperties.title')}</DrawerTitle>
        </DrawerHeader>
        <div className='flex-1 overflow-y-auto'>
          <div className='flex flex-col gap-4 px-4 py-2'>
            <div className='flex flex-col gap-1'>
              <Label>{t('landxmlProperties.layer')}</Label>
              <ID value={layer?.name ?? ''} />
            </div>
            <div className='flex flex-col gap-1'>
              <Label>{t('landxmlProperties.feature')}</Label>
              <span className='text-sm font-mono break-all'>{selected.name}</span>
            </div>
            <div className='flex flex-col gap-1'>
              <Label>{t('landxmlProperties.category')}</Label>
              <span className='text-sm font-mono'>{selected.category}</span>
            </div>

            {selected.category === 'ALIGNMENT' && (
              <div className='flex flex-col gap-1'>
                <Label>{t('landxmlProperties.station')}</Label>
                {ownAlignment && station ? (
                  <>
                    <span className='text-sm font-mono'>
                      {formatStation(internalToDisplay(ownAlignment, station.station))}
                    </span>
                    <span className='text-xs text-muted-foreground'>
                      {t('landxmlProperties.offsetFromCenterline', { offset: station.offset.toFixed(3) })}
                    </span>
                    <Button
                      variant='outline'
                      size='sm'
                      className='mt-1 self-start'
                      onClick={() => _startWalk(ownAlignment.key, station.station)}
                    >
                      {t('landxmlProperties.walk')}
                    </Button>
                  </>
                ) : (
                  <span className='text-xs text-muted-foreground'>—</span>
                )}
              </div>
            )}

            {selected.category === 'ALIGNMENT' && ownAlignment && ownAlignment.degradations.length > 0 && (
              <p data-testid='alignment-degradations' className='text-xs text-amber-700 dark:text-amber-400'>
                {tp('alignments.degraded.title', { count: ownAlignment.degradations.length })}
                {': '}
                {degradationList(ownAlignment, tp)}
              </p>
            )}

            {selected.category !== 'ALIGNMENT' && selected.nearestAlignment && nearest && (
              <div className='flex flex-col gap-1'>
                <Label>{t('landxmlProperties.nearestAlignment')}</Label>
                <span className='text-sm font-mono'>{selected.nearestAlignment.name}</span>
                <span className='text-sm font-mono'>
                  {formatStation(internalToDisplay(nearest, selected.nearestAlignment.station))}
                </span>
                <span className='text-xs text-muted-foreground'>
                  {t('landxmlProperties.offsetFromCenterline', {
                    offset: selected.nearestAlignment.offset.toFixed(3),
                  })}
                  {' · '}
                  {t('landxmlProperties.dz', { dz: selected.nearestAlignment.dz.toFixed(3) })}
                </span>
                <Button
                  variant='outline'
                  size='sm'
                  className='mt-1 self-start'
                  onClick={() => _startWalk(nearest.key, selected.nearestAlignment!.station)}
                >
                  {t('landxmlProperties.walkNearest')}
                </Button>
              </div>
            )}

            {/* Metric grid */}
            <div className='grid grid-cols-2 gap-x-4 gap-y-2 text-sm'>
              {m.length !== undefined && (
                <Metric label={t('landxmlProperties.length')} value={`${m.length.toFixed(2)} m`} />
              )}
              {m.surfaceArea !== undefined && (
                <Metric label={t('landxmlProperties.surfaceArea')} value={`${m.surfaceArea.toFixed(2)} m²`} />
              )}
              {m.parcelArea !== undefined && (
                <Metric label={t('landxmlProperties.parcelArea')} value={`${m.parcelArea.toFixed(2)} m²`} />
              )}
              {m.triangleCount !== undefined && (
                <Metric label={t('landxmlProperties.triangleCount')} value={String(m.triangleCount)} />
              )}
              {m.vertexCount !== undefined && (
                <Metric label={t('landxmlProperties.vertexCount')} value={String(m.vertexCount)} />
              )}
              {m.zMin !== undefined && (
                <Metric label={t('landxmlProperties.zMin')} value={`${m.zMin.toFixed(2)} m`} />
              )}
              {m.zMax !== undefined && (
                <Metric label={t('landxmlProperties.zMax')} value={`${m.zMax.toFixed(2)} m`} />
              )}
              {m.diameter !== undefined && (
                <Metric label={t('landxmlProperties.diameter')} value={`${(m.diameter * 1000).toFixed(0)} mm`} />
              )}
              {m.pipeShape !== undefined && (
                <Metric label={t('landxmlProperties.pipeShape')} value={m.pipeShape} />
              )}
              {m.invertStart !== undefined && (
                <Metric label={t('landxmlProperties.invertStart')} value={`${m.invertStart.toFixed(3)} m`} />
              )}
              {m.invertEnd !== undefined && (
                <Metric label={t('landxmlProperties.invertEnd')} value={`${m.invertEnd.toFixed(3)} m`} />
              )}
              {m.slope !== undefined && (
                <Metric label={t('landxmlProperties.slope')} value={`${(m.slope * 100).toFixed(2)} %`} />
              )}
              {m.rimElev !== undefined && (
                <Metric label={t('landxmlProperties.rimElev')} value={`${m.rimElev.toFixed(3)} m`} />
              )}
              {m.sumpElev !== undefined && (
                <Metric label={t('landxmlProperties.sumpElev')} value={`${m.sumpElev.toFixed(3)} m`} />
              )}
              {m.innerDiameter !== undefined && (
                <Metric
                  label={t('landxmlProperties.innerDiameter')}
                  value={`${(m.innerDiameter * 1000).toFixed(0)} mm`}
                />
              )}
            </div>
          </div>
        </div>
        <DrawerFooter>
          <Button variant='link' onClick={handleClose}>
            {t('landxmlProperties.close')}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

const Metric = ({ label, value }: { label: string; value: string }) => (
  <div className='flex flex-col gap-0.5'>
    <span className='text-xs text-muted-foreground'>{label}</span>
    <span className='font-mono text-sm break-all'>{value}</span>
  </div>
);

export default LandXMLProperties;
